import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Tabs from "./Tabs";
import axios from "../../lib/axios";
import Select from "react-select";
import { Settings, Mail, Eye, MousePointer, Zap, Save, Rocket, Play, Pause, RotateCcw, RefreshCw, CheckCircle } from "lucide-react";
import LaunchHeroCard from "./LaunchHeroCard";
import "../Pages.css";

export default function Options() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState('draft');
  const [pauseReason, setPauseReason] = useState(null);

  const [formData, setFormData] = useState({
    accountIds: [],
    stopOnReply: false,
    openTracking: false,
    linkTracking: false,
    deliveryOptimization: false,
    sendTextOnly: false,
    sendFirstEmailTextOnly: false,
    emailsPerDay: 50,
    delaySeconds: 60,
    meetingTitleOverride: "",
  });

  const handleCheckboxChange = (key) => {
    setFormData((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch email accounts
        const accRes = await axios.get("email-campaign/get-email-accounts");
        const accounts = accRes.data || [];
        setAllAccounts(
          accounts.map((acc) => ({
            value: acc.id,
            label: acc.email,
          }))
        );

        // Fetch campaign status
        const campaignsRes = await axios.get("email-campaign/get-all");
        const campaign = campaignsRes.data.find(c => c.id === id);
        if (campaign) {
          setCampaignStatus(campaign.status || 'draft');
          setPauseReason(campaign.pause_reason || null);
        }

        // Fetch existing options
        try {
          const optsRes = await axios.get(`email-campaign/get-options/${id}`);
          if (optsRes.data) {
            const o = optsRes.data;
            setFormData({
              id: id,
              accountIds: (o.accountIds || []).map((aid) => aid),
              stopOnReply: o.stopOnReply,
              openTracking: o.openTracking,
              linkTracking: o.linkTracking,
              deliveryOptimization: o.deliveryOptimization,
              sendTextOnly: o.sendTextOnly,
              sendFirstEmailTextOnly: o.sendFirstEmailTextOnly,
              emailsPerDay: o.emailsPerDay,
              delaySeconds: o.delaySeconds,
              meetingTitleOverride: o.meetingTitleOverride || "",
            });
          }
        } catch (err) {
          // Options might not exist yet, that's okay
          console.log("No existing options found");
        }
      } catch (err) {
        console.error("Error loading options:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        id: id,
        accountIds: formData.accountIds,
        stopOnReply: formData.stopOnReply,
        openTracking: formData.openTracking,
        linkTracking: formData.linkTracking,
        deliveryOptimization: formData.deliveryOptimization,
        sendTextOnly: formData.sendTextOnly,
        sendFirstEmailTextOnly: formData.sendFirstEmailTextOnly,
        emailsPerDay: formData.emailsPerDay,
        delaySeconds: formData.delaySeconds,
        meetingTitleOverride: formData.meetingTitleOverride?.trim() || null,
      };

      await axios.post(`email-campaign/options/${id}`, payload);
      alert("Options saved successfully!");
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save options");
    } finally {
      setSaving(false);
    }
  };

  const handleLaunchCampaign = async () => {
    // First save the options
    setLaunching(true);
    try {
      const payload = {
        id: id,
        accountIds: formData.accountIds,
        stopOnReply: formData.stopOnReply,
        openTracking: formData.openTracking,
        linkTracking: formData.linkTracking,
        deliveryOptimization: formData.deliveryOptimization,
        sendTextOnly: formData.sendTextOnly,
        sendFirstEmailTextOnly: formData.sendFirstEmailTextOnly,
        emailsPerDay: formData.emailsPerDay,
        delaySeconds: formData.delaySeconds,
        meetingTitleOverride: formData.meetingTitleOverride?.trim() || null,
      };

      await axios.post(`email-campaign/options/${id}`, payload);

      // Then start the campaign
      await axios.post(`email-campaign/${id}/start`);

      setCampaignStatus('active');
      alert("Campaign launched successfully!");
      navigate('/campaigns');
    } catch (err) {
      console.error("Launch failed:", err);
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        alert('Cannot launch campaign:\n\n' + detail.errors.map(e => `• ${e}`).join('\n'));
      } else {
        alert(typeof detail === 'string' ? detail : 'Failed to launch campaign');
      }
    } finally {
      setLaunching(false);
    }
  };

  const handlePause = async () => {
    setLaunching(true);
    try {
      await axios.post(`email-campaign/${id}/pause`);
      setCampaignStatus('paused');
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to pause campaign");
    } finally {
      setLaunching(false);
    }
  };

  const handleResume = async () => {
    setLaunching(true);
    try {
      await axios.post(`email-campaign/${id}/resume`);
      setCampaignStatus('active');
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to resume campaign");
    } finally {
      setLaunching(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    setCampaignStatus(newStatus);
  };

  if (loading) {
    return (
      <div className="page animate-fade-in">
        <Tabs activeTab="Options" campaignId={id} onStatusChange={handleStatusChange} />
        <div className="card">
          <div style={{ padding: '4rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="loader"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page animate-fade-in">
      <Tabs activeTab="Options" campaignId={id} onStatusChange={handleStatusChange} />

      <div className="page-header">
        <div className="header-info">
          <h2>Campaign Options</h2>
          <p>Configure tracking, delivery, and sending settings</p>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '0.75rem' }}>
          {/* Save button - always visible */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-ghost"
          >
            {saving ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            Save Options
          </button>

          {/* Launch/Pause/Resume buttons based on status */}
          {launching ? (
            <button className="btn btn-primary" disabled style={{ minWidth: '160px' }}>
              <RefreshCw size={16} className="animate-spin" />
            </button>
          ) : (
            <>
              {campaignStatus === 'draft' && (
                <button
                  onClick={handleLaunchCampaign}
                  className="btn"
                  style={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: 'white',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                  }}
                >
                  <Rocket size={16} />
                  Launch Campaign
                </button>
              )}
              {campaignStatus === 'active' && (
                <button
                  onClick={handlePause}
                  className="btn"
                  style={{
                    background: 'rgba(234, 179, 8, 0.1)',
                    color: '#ca8a04',
                    border: '1px solid rgba(234, 179, 8, 0.3)'
                  }}
                >
                  <Pause size={16} />
                  Pause Campaign
                </button>
              )}
              {campaignStatus === 'paused' && (
                <button
                  onClick={handleResume}
                  className="btn"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                  }}
                >
                  <RotateCcw size={16} />
                  Resume Campaign
                </button>
              )}
              {campaignStatus === 'completed' && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'rgba(99, 102, 241, 0.1)',
                  color: '#6366f1',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: '600'
                }}>
                  <CheckCircle size={16} />
                  Campaign Completed
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Accounts to use */}
        <div className="card">
          <div className="card-header">
            <h3>
              <Mail size={18} />
              Email Accounts
            </h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>Select email accounts to use for this campaign</label>
              <Select
                isMulti
                name="accountIds"
                options={allAccounts}
                className="basic-multi-select"
                classNamePrefix="select"
                value={allAccounts.filter((opt) =>
                  formData.accountIds.includes(opt.value)
                )}
                onChange={(selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    accountIds: selected.map((s) => s.value),
                  }));
                }}
                placeholder="Select email accounts…"
                styles={{
                  control: (base) => ({
                    ...base,
                    background: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    minHeight: '44px',
                    '&:hover': {
                      borderColor: 'var(--color-border)'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    background: 'var(--color-bg-card)',
                    border: '1px solid var(--color-border)'
                  }),
                  option: (base, state) => ({
                    ...base,
                    background: state.isFocused ? 'var(--color-bg-hover)' : 'transparent',
                    color: 'var(--color-text-primary)',
                    '&:active': {
                      background: 'var(--color-bg-hover)'
                    }
                  })
                }}
              />
            </div>

            <div className="toggle-item" style={{ marginTop: '1rem' }}>
              <div className="toggle-info">
                <div className="toggle-label">Stop sending on reply</div>
                <div className="toggle-desc">Automatically stop the sequence when a recipient replies</div>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={formData.stopOnReply}
                  onChange={() => handleCheckboxChange("stopOnReply")}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Tracking */}
        <div className="card">
          <div className="card-header">
            <h3>
              <Eye size={18} />
              Tracking Settings
            </h3>
          </div>
          <div className="card-body">
            <div className="toggle-list">
              <div className="toggle-item">
                <div className="toggle-info">
                  <div className="toggle-label">Open tracking</div>
                  <div className="toggle-desc">Track when recipients open your emails</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.openTracking}
                    onChange={() => handleCheckboxChange("openTracking")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="toggle-item">
                <div className="toggle-info">
                  <div className="toggle-label">Link tracking</div>
                  <div className="toggle-desc">Track when recipients click links in your emails</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.linkTracking}
                    onChange={() => handleCheckboxChange("linkTracking")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Optimization */}
        <div className="card">
          <div className="card-header">
            <h3>
              <Settings size={18} />
              Delivery Optimization
            </h3>
          </div>
          <div className="card-body">
            <div className="toggle-list">
              <div className="toggle-item">
                <div className="toggle-info">
                  <div className="toggle-label">Disable tracking for deliverability</div>
                  <div className="toggle-desc">Remove tracking pixels to improve inbox placement</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.deliveryOptimization}
                    onChange={() => handleCheckboxChange("deliveryOptimization")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="toggle-item">
                <div className="toggle-info">
                  <div className="toggle-label">Send emails as text-only</div>
                  <div className="toggle-desc">Send all emails in plain text format</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.sendTextOnly}
                    onChange={() => handleCheckboxChange("sendTextOnly")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="toggle-item">
                <div className="toggle-info">
                  <div className="toggle-label">Send first email as text-only</div>
                  <div className="toggle-desc">Only the first email will be sent in plain text</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.sendFirstEmailTextOnly}
                    onChange={() => handleCheckboxChange("sendFirstEmailTextOnly")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Sending Speed */}
        <div className="card">
          <div className="card-header">
            <h3>
              <Zap size={18} />
              Sending Speed
            </h3>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label>Emails per day</label>
                <input
                  type="number"
                  min="1"
                  name="emailsPerDay"
                  value={formData.emailsPerDay}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Delay between emails (seconds)</label>
                <input
                  type="number"
                  min="0"
                  name="delaySeconds"
                  value={formData.delaySeconds}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Meeting title override</label>
                <input
                  type="text"
                  maxLength={255}
                  name="meetingTitleOverride"
                  placeholder="{campaign}: call with {prospect}"
                  value={formData.meetingTitleOverride}
                  onChange={handleInputChange}
                />
                <small style={{ display: 'block', marginTop: 6, opacity: 0.7 }}>
                  For Reply Agent auto-booked meetings on this campaign. Placeholders:{' '}
                  <code>{'{campaign}'}</code> <code>{'{rep}'}</code>{' '}
                  <code>{'{prospect}'}</code> <code>{'{company}'}</code>. Leave blank
                  to use the Reply Agent default.
                </small>
              </div>
            </div>
          </div>
        </div>

        <LaunchHeroCard
          campaignId={id}
          campaignStatus={campaignStatus}
          formData={formData}
          onLaunch={handleLaunchCampaign}
          onPause={handlePause}
          onResume={handleResume}
          launching={launching}
          pauseReason={pauseReason}
        />
      </div>
    </div>
  );
}
